<template>
  <app-field
    v-slot="{ describedBy: fieldDescribedBy }"
    :label="label"
    :label-for="inputId"
    :help="help"
    :hint="hint"
    :tooltip="tooltip"
    :error="fieldError"
    :success="success"
    :required="required"
    :optional="optional"
    :described-by="ariaDescribedby"
    :label-class="labelClass"
    :container-class="containerClass"
  >
    <div class="app-input relative w-full" :class="[wrapperClass, { 'app-input--number': isNumeric, 'app-input--unit': passiveSuffix, 'app-input--short-unit': passiveSuffix && suffixText.length <= 3 }]">
      <span
        v-if="hasPrefix"
        class="pointer-events-none absolute inset-y-0 z-10 flex items-center text-theme-text-muted"
        :class="{ 'justify-center': prefixIcon && !prefixText && !$slots.prefix }"
        :style="prefixInsetStyle"
      >
        <slot name="prefix">
          <Icon
            v-if="prefixIcon"
            :path="prefixIcon"
            :class="tokens.icon"
          />
          <span v-else-if="prefixText" :class="addonTextClass">{{ prefixText }}</span>
        </slot>
      </span>

      <input
        v-bind="attrs"
        :id="inputId"
        ref="inputEl"
        :name="name"
        :value="isNumeric ? draft : modelValue"
        :type="resolvedType"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :spellcheck="spellcheck"
        :autofocus="autofocus"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :step="step"
        :inputmode="isNumeric ? (precision === 0 ? 'numeric' : 'decimal') : attrs.inputmode"
        :aria-describedby="fieldDescribedBy"
        :aria-invalid="fieldError ? true : attrs['aria-invalid']"
        :class="controlClass"
        :style="iconInputStyle"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @keydown="handleKeydown"
        @invalid="handleInvalid"
        @compositionstart="composing = true"
        @compositionend="endComposition"
      />

      <span
        v-if="clearable && hasValue && !disabled && !readonly"
        class="app-input-clear absolute inset-y-0 z-10 flex items-center"
        :class="[tokens.iconGutter]"
        :style="suffixInsetStyle"
      >
        <AppButton
          class="pointer-events-auto text-theme-text-muted transition hover:text-theme-text-secondary"
          variant="ghost"
          tone="neutral"
          size="xs"
          icon-only
          :aria-label="$t('forms.clear')"
          @click="clearValue"
        >
          <Icon :path="mdiClose" :class="tokens.icon" />
        </AppButton>
      </span>

      <span v-else-if="passiveSuffix" :id="`${inputId}-unit`" class="app-input__unit">
        {{ suffixText }}
      </span>
      <span
        v-else-if="$slots.suffix || suffixIcon || suffixText"
        class="app-input-suffix absolute inset-y-0 z-10 flex items-center"
        :class="[
          suffixIcon && !$slots.suffix && !suffixText ? tokens.iconGutter : null,
          suffixClickable ? null : 'pointer-events-none',
        ]"
        :style="suffixInsetStyle"
      >
        <AppButton
          class="text-theme-text-muted transition"
          :class="suffixClickable ? 'pointer-events-auto hover:text-theme-text-secondary !h-11 !w-11 md:!h-control-xs md:!w-control-xs' : 'pointer-events-none'"
          variant="ghost"
          tone="neutral"
          size="xs"
          :icon-only="Boolean(suffixIcon) && !$slots.suffix && !suffixText"
          :disabled="!suffixClickable"
          :aria-label="suffixLabel || (type === 'password' || autocomplete.includes('password') ? $t('forms.toggle_password') : undefined)"
          @click="handleSuffixClick"
        >
          <slot name="suffix">
            <Icon
              v-if="suffixIcon"
              :path="suffixIcon"
              :class="tokens.icon"
            />
            <span v-else-if="suffixText" :class="addonTextClass">{{ suffixText }}</span>
          </slot>
        </AppButton>
      </span>
    </div>
  </app-field>
</template>

<script>
import { computed, ref, useAttrs, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiClose } from '@mdi/js'

import AppButton from '@/components/ui/buttons/AppButton.vue'
import AppField from '@/components/ui/forms/AppField.vue'
import Icon from '@/components/Icon.vue'
import { getFormControlId, mergeDescribedBy, useFormControlStyles } from '@/composables/useFormControlStyles'
import { hasValidationError } from '@/composables/useValidationState'
import { parseFormNumber } from '@/domain/formNumbers'

export default {
  name: 'AppInput',
  components: {
    AppButton,
    AppField,
    Icon
  },
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Number], default: '' },
    label: { type: String, default: '' },
    help: { type: String, default: '' },
    hint: { type: String, default: '' },
    tooltip: { type: String, default: '' },
    error: { type: [String, Boolean, Array], default: false },
    validationMessage: { type: String, default: '' },
    success: { type: [String, Boolean], default: false },
    size: { type: String, default: 'md' },
    variant: { type: String, default: 'default' },
    disabled: Boolean,
    readonly: Boolean,
    required: Boolean,
    optional: Boolean,
    name: { type: String, default: '' },
    id: { type: String, default: '' },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    autocomplete: { type: String, default: '' },
    prefixIcon: { type: String, default: '' },
    suffixIcon: { type: String, default: '' },
    prefixText: { type: String, default: '' },
    suffixText: { type: String, default: '' },
    suffixLabel: { type: String, default: '' },
    clearable: Boolean,
    suffixClickable: Boolean,
    spellcheck: { type: [Boolean, String], default: undefined },
    autofocus: Boolean,
    maxlength: { type: [String, Number], default: undefined },
    min: { type: [String, Number], default: undefined },
    max: { type: [String, Number], default: undefined },
    step: { type: [String, Number], default: undefined },
    precision: { type: Number, default: undefined },
    inputClass: { type: [String, Array, Object], default: '' },
    wrapperClass: { type: [String, Array, Object], default: '' },
    labelClass: { type: [String, Array, Object], default: '' },
    containerClass: { type: [String, Array, Object], default: '' },
    describedBy: { type: String, default: '' }
  },
  emits: [
    'update:modelValue',
    'input',
    'focus',
    'blur',
    'change',
    'keydown',
    'clear',
    'suffix-click',
    'validity-change'
  ],
  setup (props, { emit, expose, slots }) {
    const attrs = useAttrs()
    const { t, locale } = useI18n()
    const inputEl = ref(null)
    const generatedId = getFormControlId('app-input')

    const isNumeric = computed(() => props.type === 'number')
    function committedText(value) {
      const text = String(value ?? '');
      if (!isNumeric.value || typeof value !== 'number') return text;
      const decimal = new Intl.NumberFormat(locale.value).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
      return text.replace('.', decimal);
    }
    const draft = ref(committedText(props.modelValue))
    const showValidation = ref(false)
    const composing = ref(false)
    let lastEmitted
    const parsed = computed(() => parseFormNumber(draft.value, props))
    const numericError = computed(() => isNumeric.value && parsed.value.error
      ? t(`forms.number_${parsed.value.error}`, { min: props.min ?? 0, max: props.max, precision: props.precision, step: props.step })
      : props.validationMessage)
    const nativeError = ref('')
    const fieldError = computed(() => hasValidationError(props.error) ? props.error : (showValidation.value ? numericError.value || nativeError.value || false : false))
    const { tokens, inputBaseClass, addonTextClass } = useFormControlStyles(props, fieldError)

    const inputId = computed(() => props.id || generatedId)
    const hasPrefix = computed(() => Boolean(slots.prefix || props.prefixText || props.prefixIcon))
    const passiveSuffix = computed(() => Boolean(props.suffixText && !props.suffixIcon && !props.suffixClickable && !slots.suffix && !props.clearable))
    const hasSuffix = computed(() => !passiveSuffix.value && Boolean(slots.suffix || props.suffixIcon || props.suffixText || props.clearable))
    const hasValue = computed(() => (isNumeric.value ? draft.value : String(props.modelValue ?? '')) !== '')
    const resolvedType = computed(() => isNumeric.value ? 'text' : (props.type || 'text'))
    const ariaDescribedby = computed(() => mergeDescribedBy(props.describedBy, attrs['aria-describedby'], passiveSuffix.value && `${inputId.value}-unit`))

    watch(() => props.modelValue, (value) => {
      // A parent using v-model.number may echo a number; retain the exact text/caret
      // for our own updates, but still accept external loads, resets and presets.
      const numericEcho = typeof value === 'number' && value === parseFloat(lastEmitted)
      if (String(value ?? '') !== lastEmitted && !numericEcho) {
        draft.value = committedText(value)
        nativeError.value = ''
      }
    })
    watchEffect(() => {
      // `error` also carries server responses. It must not prevent a corrected
      // value being resubmitted to the server; local constraints use validationMessage.
      inputEl.value?.setCustomValidity(numericError.value)
    })
    watch(numericError, (message) => emit('validity-change', message), { immediate: true })
    const insetTokenBySize = {
      xs: '--space-control-x-xs',
      sm: '--space-control-x-sm',
      md: '--space-control-x-md',
      lg: '--space-control-x-lg'
    }
    const insetToken = computed(() => insetTokenBySize[props.size] || insetTokenBySize.md)
    const iconSizeToken = computed(() => `var(--size-icon-${['xs', 'sm', 'md', 'lg'].includes(props.size) ? props.size : 'md'})`)
    const prefixInsetStyle = computed(() => ({
      left: `var(${insetToken.value})`,
      width: props.prefixIcon && !props.prefixText && !slots.prefix ? iconSizeToken.value : undefined
    }))
    const iconInputStyle = computed(() => props.prefixIcon && !props.prefixText && !slots.prefix
      ? { paddingLeft: `calc(2 * var(${insetToken.value}) + ${iconSizeToken.value})` }
      : undefined)
    const suffixInsetStyle = computed(() => ({
      right: `var(${insetToken.value})`,
      justifyContent: 'center'
    }))

    const controlClass = computed(() => {
      const classes = [...inputBaseClass.value]

      if (hasPrefix.value) {
        classes.push(props.prefixText ? tokens.value.inputPaddingLeftText : tokens.value.inputPaddingLeftIcon)
      } else {
        classes.push(tokens.value.inputPadding)
      }

      if (hasSuffix.value) {
        classes.push(props.suffixText ? tokens.value.inputPaddingRightText : tokens.value.inputPaddingRightIcon)
      }

      if (props.inputClass) {
        classes.push(props.inputClass)
      }

      return classes
    })

    function handleInput (event) {
      if (composing.value) return
      draft.value = event.target.value
      readNativeError(event)
      // Emit canonical decimal text; keep malformed/incomplete input intact.
      // Feature validation and native validity must reject it before submission.
      lastEmitted = isNumeric.value && parsed.value.value !== null
        ? parsed.value.text
        : event.target.value
      emit('update:modelValue', lastEmitted)
      emit('input', event)
    }

    function endComposition (event) {
      composing.value = false
      handleInput(event)
    }

    function handleFocus (event) {
      emit('focus', event)
    }

    function handleBlur (event) {
      // Let the action validate. Adding an error line during mouse-down can
      // move the clicked button before mouse-up and swallow the first click.
      if (!event.relatedTarget?.closest('button, [role="button"], a[href]')) showValidation.value = true
      readNativeError(event)
      if (isNumeric.value && !parsed.value.error && parsed.value.text) {
        const separator = new Intl.NumberFormat(locale.value).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.'
        draft.value = parsed.value.text.replace('.', separator)
      }
      emit('blur', event)
    }

    function readNativeError(event) {
      // Custom numeric/cross-field errors are reactive above. Read only native
      // text/email/pattern errors here so an old custom message cannot survive correction.
      nativeError.value = !isNumeric.value && !event.target.validity.customError
        ? event.target.validationMessage : ''
    }

    function handleInvalid(event) {
      showValidation.value = true
      readNativeError(event)
    }

    function handleChange (event) {
      emit('change', event)
    }

    function handleKeydown (event) {
      emit('keydown', event)
    }

    function clearValue () {
      draft.value = ''
      nativeError.value = ''
      lastEmitted = ''
      emit('update:modelValue', '')
      emit('clear')
      inputEl.value?.focus()
    }

    function handleSuffixClick (event) {
      if (!props.suffixClickable) {
        return
      }

      emit('suffix-click', event)
    }

    expose({
      inputEl,
      focus: () => inputEl.value?.focus(),
      blur: () => inputEl.value?.blur(),
      select: () => inputEl.value?.select(),
      setSelectionRange: (...args) => inputEl.value?.setSelectionRange?.(...args),
      validate: () => {
        showValidation.value = true
        return inputEl.value?.checkValidity() ?? false
      },
      get selectionStart () {
        return inputEl.value?.selectionStart ?? null
      },
      get selectionEnd () {
        return inputEl.value?.selectionEnd ?? null
      }
    })

    return {
      attrs,
      mdiClose,
      addonTextClass,
      tokens,
      inputEl,
      inputId,
      hasValue,
      hasPrefix,
      passiveSuffix,
      isNumeric,
      draft,
      fieldError,
      showValidation,
      composing,
      endComposition,
      resolvedType,
      ariaDescribedby,
      prefixInsetStyle,
      iconInputStyle,
      suffixInsetStyle,
      controlClass,
      handleInput,
      handleFocus,
      handleBlur,
      handleInvalid,
      handleChange,
      handleKeydown,
      clearValue,
      handleSuffixClick
    }
  }
}
</script>
